/**
 * Utility functions for HubSpot integration
 */

/**
 * Generate HubSpot deal URL for the given deal ID
 * @param dealId - The HubSpot deal ID
 * @returns The complete HubSpot deal URL
 */
export const generateHubSpotDealUrl = (dealId: string): string => {
  const hubspotPortalId = '4950983'; // SmartCat's HubSpot portal ID
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-3/${dealId}`;
};

/**
 * Generate HubSpot sales cycle URL for the given sales cycle ID
 * @param salesCycleId - The HubSpot sales cycle ID
 * @returns The complete HubSpot sales cycle URL
 */
export const generateHubSpotSalesCycleUrl = (salesCycleId: string): string => {
  const hubspotPortalId = '4950983'; // SmartCat's HubSpot portal ID
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/2-44385613/${salesCycleId}`;
};

/**
 * Open HubSpot deal in a new tab
 * @param dealId - The HubSpot deal ID
 */
export const openHubSpotDeal = (dealId: string): void => {
  const url = generateHubSpotDealUrl(dealId);
  console.log('Opening HubSpot deal URL:', url);
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    console.warn('Popup blocked - new tab could not be opened');
  }
};

/**
 * Open HubSpot sales cycle in a new tab
 * @param salesCycleId - The HubSpot sales cycle ID
 */
export const openHubSpotSalesCycle = (salesCycleId: string): void => {
  const url = generateHubSpotSalesCycleUrl(salesCycleId);
  console.log('Opening HubSpot sales cycle URL:', url);
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    console.warn('Popup blocked - new tab could not be opened');
  }
};

/**
 * Generate HubSpot company URL for the given company ID
 * @param companyId - The HubSpot company ID
 * @returns The complete HubSpot company URL
 */
export const generateHubSpotCompanyUrl = (companyId: string): string => {
  const hubspotPortalId = '4950983'; // SmartCat's HubSpot portal ID
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-2/${companyId}`;
};

/**
 * Open HubSpot company page in a new tab
 * @param companyId - The HubSpot company ID
 */
export const openHubSpotCompany = (companyId: string): void => {
  const url = generateHubSpotCompanyUrl(companyId);
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    console.warn('Popup blocked - new tab could not be opened');
  }
};

/**
 * Generate HubSpot deals list URL filtered by company
 * @param companyId - The HubSpot company ID
 * @returns The complete HubSpot deals list URL filtered for this company
 */
export const generateHubSpotCompanyDealsListUrl = (companyId: string): string => {
  const hubspotPortalId = '4950983';
  const filters = JSON.stringify([{operator: 'EQ', value: companyId, property: 'associations.0-341'}]);
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/objects/0-3/views/all/list?filters=${encodeURIComponent(filters)}&pipelineId=all`;
};

/**
 * Open HubSpot deals list filtered by company in a new tab
 * @param companyId - The HubSpot company ID
 */
export const openCompanyDeals = (companyId: string): void => {
  const url = generateHubSpotCompanyDealsListUrl(companyId);
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    console.warn('Popup blocked - new tab could not be opened');
  }
};

/**
 * Handle company name click - opens HubSpot deal if deal_id is available, or sales cycle if sales_cycle_id is available
 * @param dealId - The HubSpot deal ID (optional)
 * @param salesCycleId - The HubSpot sales cycle ID (optional)
 * @param companyName - The company name for logging/fallback
 */
export const handleCompanyNameClick = (dealId?: string, salesCycleId?: string, companyName?: string): void => {
  console.log('handleCompanyNameClick called with:', { dealId, salesCycleId, companyName });
  
  if (dealId) {
    console.log(`Opening HubSpot deal ${dealId} for company: ${companyName}`);
    openHubSpotDeal(dealId);
  } else if (salesCycleId) {
    console.log(`Opening HubSpot sales cycle ${salesCycleId} for company: ${companyName}`);
    openHubSpotSalesCycle(salesCycleId);
  } else {
    console.warn(`No deal_id or sales_cycle_id available for company: ${companyName}`);
  }
};